import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Divider, Stack, TextField, Typography } from '@mui/material';

import type { ChecklistRuleDataPoint } from './ChecklistRuleCard';

interface Props {
  dataPoint: ChecklistRuleDataPoint;
  showDivider: boolean;
}

export default function DataPointRow({ dataPoint, showDivider }: Props) {
  const [othersExpanded, setOthersExpanded] = useState(false);
  const editable = isDataPointEditable(dataPoint);
  const otherItems = dataPoint.expandableItems ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ px: 2, py: 1.25 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 240px', fontWeight: 500 }}>
          {dataPoint.label}
        </Typography>
        {editable ? (
          <TextField
            error={dataPoint.error}
            onChange={(event) => dataPoint.onChange?.(event.target.value)}
            size="small"
            value={dataPoint.value}
            InputProps={{
              sx: dataPoint.error ? { color: 'error.main' } : undefined,
            }}
            inputProps={{ 'aria-invalid': dataPoint.error }}
            fullWidth
          />
        ) : (
          <Box sx={{ flex: 1, py: 1 }}>
            <Typography variant="body2" color={dataPoint.error ? 'error.main' : 'text.primary'} component="span">
              {dataPoint.value}
            </Typography>
            {otherItems.length > 0 && (
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setOthersExpanded((current) => !current)}
                  endIcon={
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{ transform: othersExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    />
                  }
                  sx={{ ml: 1, p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
                >
                  Show details
                </Button>
                <Collapse in={othersExpanded}>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {otherItems.map((item) => (
                      <Typography key={item} variant="body2" color="text.secondary">
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Collapse>
              </>
            )}
          </Box>
        )}
      </Stack>
      {showDivider && <Divider />}
    </Box>
  );
}

function isDataPointEditable(dataPoint: ChecklistRuleDataPoint) {
  if (dataPoint.readOnly != null) {
    return !dataPoint.readOnly;
  }

  return !!dataPoint.onChange;
}
